import { useEffect, useState } from "react";
import axios from "axios";
import Lenis from "lenis";
import { API, adminApi, imgUrl, track } from "@/lib/api";
import { Header } from "@/components/site/Header";
import { Hero } from "@/components/site/Hero";
import { AboutSection } from "@/components/site/AboutSection";
import { ServicesSection } from "@/components/site/ServicesSection";
import { AwardsSection } from "@/components/site/AwardsSection";
import { ReviewsSection } from "@/components/site/ReviewsSection";
import { GallerySection } from "@/components/site/GallerySection";
import { LibrarySection } from "@/components/site/LibrarySection";
import { ContactSection } from "@/components/site/ContactSection";
import { Footer } from "@/components/site/Footer";
import { MobileWhatsAppBar } from "@/components/site/WhatsApp";

const SECTIONS = {
  about: AboutSection, services: ServicesSection, awards: AwardsSection, reviews: ReviewsSection,
  gallery: GallerySection, library: LibrarySection, contact: ContactSection,
};

const setMeta = (c) => {
  const s = c?.settings || {};
  document.title = s.site_title || c?.profile?.name || "Clinic";
  let m = document.querySelector('meta[name="description"]');
  if (m && s.meta_description) m.setAttribute("content", s.meta_description);
  const logo = c?.profile?.logo;
  if (logo) {
    let l = document.querySelector('link[rel="icon"]');
    if (!l) { l = document.createElement("link"); l.rel = "icon"; document.head.appendChild(l); }
    l.href = imgUrl(logo);
  }
  if (s.ga_id && !document.getElementById("ga-script")) {
    const sc = document.createElement("script");
    sc.id = "ga-script"; sc.async = true; sc.src = `https://www.googletagmanager.com/gtag/js?id=${s.ga_id}`;
    document.head.appendChild(sc);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag("js", new Date()); window.gtag("config", s.ga_id);
  }
};

export default function PublicSite() {
  const [c, setC] = useState(null);
  const [failed, setFailed] = useState(false);
  const preview = new URLSearchParams(window.location.search).get("preview") === "1";

  useEffect(() => {
    const req = preview && localStorage.getItem("admin_token") ? adminApi.get("/admin/content") : axios.get(`${API}/content`);
    req.then((r) => { const content = r.data?.content || {}; setC(content); setMeta(content); }).catch(() => setFailed(true));
    if (!preview && !sessionStorage.getItem("viewed")) { sessionStorage.setItem("viewed", "1"); track("view"); }
  }, [preview]);

  useEffect(() => {
    if (!c) return undefined;
    const lenis = new Lenis({ duration: 1.1, anchors: { offset: -72 } });
    let id;
    const raf = (t) => { lenis.raf(t); id = requestAnimationFrame(raf); };
    id = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(id); lenis.destroy(); };
  }, [c]);

  if (failed) return <div className="flex min-h-screen items-center justify-center bg-sky-50 p-6 text-center text-slate-600">The website could not load right now. Please refresh in a moment.</div>;
  if (!c) return <div className="flex min-h-screen items-center justify-center bg-sky-50"><div className="h-10 w-10 animate-spin rounded-full border-4 border-sky-200 border-t-blue-900" /></div>;

  const sections = (c.sections || []).filter((s) => s.visible !== false && SECTIONS[s.id]);
  return (
    <div className="min-h-screen bg-white pb-24 md:pb-0" data-testid="public-site">
      {preview && (
        <div className="sticky top-0 z-[60] bg-orange-500 px-4 py-2 text-center text-sm font-semibold text-white" data-testid="preview-banner">
          Preview of your saved draft — visitors don't see these changes until you publish.
        </div>
      )}
      <Header c={c} />
      <main>
        <Hero c={c} />
        {sections.map((s) => { const S = SECTIONS[s.id]; return <S key={s.id} c={c} s={s} />; })}
      </main>
      <Footer c={c} />
      <MobileWhatsAppBar wa={c.whatsapp} />
    </div>
  );
}
